import { colorFor } from "@/lib/collab";
import { initials } from "@/lib/format";
import { useRow } from "@/lib/store";
import type { Id } from "@/lib/types";
import { cn } from "@/lib/utils";
import { UserRound } from "lucide-react";

const SIZES = { xs: "size-4 text-[8px]", sm: "size-5 text-[9px]", md: "size-6 text-[10px]", lg: "size-8 text-xs", xl: "size-12 text-base" };

export function Avatar({ name, src, id, size = "sm", className }: { name: string; src?: string | null; id: string; size?: keyof typeof SIZES; className?: string }) {
  if (src) return <img src={src} alt="" className={cn("shrink-0 rounded-full object-cover", SIZES[size], className)} referrerPolicy="no-referrer" />;
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white select-none", SIZES[size], className)} style={{ background: colorFor(id) }} aria-hidden>
      {initials(name)}
    </span>
  );
}

export function UserAvatar({ userId, size = "sm", className, showUnassigned = true }: { userId: Id | null | undefined; size?: keyof typeof SIZES; className?: string; showUnassigned?: boolean }) {
  const user = useRow("user", userId);
  if (!user) {
    if (!showUnassigned) return null;
    return (
      <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/50 text-muted-foreground", SIZES[size], className)} title="Unassigned">
        <UserRound className="size-[70%]" />
      </span>
    );
  }
  return <Avatar name={user.name} src={user.avatarUrl} id={user.id} size={size} className={className} />;
}

export function UserName({ userId, fallback = "Unassigned" }: { userId: Id | null | undefined; fallback?: string }) {
  const user = useRow("user", userId);
  return <>{user?.name ?? fallback}</>;
}

export function AvatarStack({ ids, max = 4, size = "sm" }: { ids: Id[]; max?: number; size?: keyof typeof SIZES }) {
  const shown = ids.slice(0, max);
  return (
    <span className="flex items-center -space-x-1.5">
      {shown.map((id) => (
        <UserAvatar key={id} userId={id} size={size} className="ring-2 ring-background" />
      ))}
      {ids.length > max && <span className="pl-2.5 text-xs text-muted-foreground">+{ids.length - max}</span>}
    </span>
  );
}
